import React, { useEffect, useCallback, useRef, useState } from 'react';
import LifelineOrderDetails from '../../../containers/lifeline/lifelineOrderDetails';
import './LifelineDashboard.scss';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { EmptyState } from '../../../components/commons';
import { Loader } from '../../../UiKit';
import { hasCookieToken } from '../../../utils/CookieToken';
import { homePageImg, InvalidSearchImg } from '../../../images/images';
import {
  getSearchOrders,
  setLoading,
  showBlockingPopup,
} from '../../../store/orders/actions';
import { useGetRoles } from '../../../store/auth/actions';
import { getRefundWarnings } from '../../../store/basicOrder/actions';
import { BlockingPopupWrapper } from '../../../components/widgets';
import { sendLoadMoreOrderEvents } from '../../../helpers/analytics/search.analytics';
import { NewUser } from '../../../components/lifeline/orderDetails/NewUser/NewUser';
import HelpDeskV2 from '../../../components/HelpDeskV2';
import { loadMoreOrdersEvent } from '../../../analytics/events';
import getStore from '../../../store/store';
import history from '../../../history';
import { MetricsForJumboEvent } from '../../../analytics/constants';
import { Plus } from 'lucide-react';
import Button from '../../../components/ComponentsV1/Button';
import { LifelineHeaderV1 } from '../../../components/lifeline/header/LifelineHeader/LifelineHeaderV1';

const getErrorSubheading = (error) => {
  if (!error.uuid) return 'Please try again later';

  if (error.uuid === 'NA') return '';

  return `Track ID: ${error.uuid}`;
};

const LifelineDashboard = (props) => {
  const {
    isShowEmptyState,
    isSearchInvalid,
    isBlockingPopup,
    isLoading,
    orders,
    hasMoreOrders,
    nextCursor,
    error,
    announcements,
    alerts,
    showBlockingPopup,
    searchType,
    searchValue,
    newUserData = null,
    isWFH = false,
    setLoading = () => {},
    getSearchOrders = () => {},
    getRefundWarnings = () => {},
    useGetRoles = () => {},
  } = props;

  const [refundWarning, setRefundWarning] = useState(null);
  const [isLoadingMoreOrders, setIsLoadingMoreOrders] = useState(false);
  const [openHelpSection, setOpenHelpSection] = useState(false);
  const [helpDSectionOrderId, setHelpDSectionOrderId] = useState(null);
  const store = getStore(history);
  const state = store.getState();
  const { ordersState } = state;

  const headerRef = useRef(null);

  const isAuthenticated = hasCookieToken;

  useGetRoles({});

  useEffect(() => {
    document.title = 'Lifeline Dashboard';
    setLoading(false);
  }, [setLoading]);

  const fetchRefundWarnings = useCallback(() => {
    const user_id = orders?.[0]?.user?.id;
    if (!user_id) return;
    getRefundWarnings(user_id).then((response) => {
      setRefundWarning(response);
    });
  }, [orders, getRefundWarnings, setRefundWarning]);

  useEffect(() => {
    if (orders?.length) fetchRefundWarnings();
  }, [orders, fetchRefundWarnings]);

  useEffect(() => {
    if (orders) {
      if (isLoadingMoreOrders) {
        setIsLoadingMoreOrders(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const loadMoreOrders = () => {
    setIsLoadingMoreOrders(true);
    sendLoadMoreOrderEvents();
    setLoading(true);
    let queryStrings = {};
    queryStrings['type'] = searchType;
    queryStrings['value'] = searchValue;
    queryStrings['offset'] = orders.length;
    queryStrings['next_cursor'] = nextCursor;
    getSearchOrders(queryStrings);
    loadMoreOrdersEvent({
      [MetricsForJumboEvent.customer_id]: ordersState?.customer_id,
    });
  };

  return (
    <div className='lifelineDashboard'>
      <LifelineHeaderV1
        ref={headerRef}
        isAuthenticated={isAuthenticated}
        announcements={announcements}
      />

      {/* Main Lifeline Content */}
      <div className='content'>
        {!isAuthenticated && !isLoading && (
          <EmptyState
            img={homePageImg}
            heading='Sign up or login to blinkit'
            subHeading="You're just one step away from Lifeline"
          />
        )}

        {error && isAuthenticated && (
          <EmptyState
            img={InvalidSearchImg}
            heading={`${
              error.uuid && error.uuid.length
                ? error.message
                : 'Something went wrong'
            }`}
            subHeading={getErrorSubheading(error)}
          />
        )}

        {isLoading && (
          <div className='loader'>
            <Loader />
          </div>
        )}

        {isAuthenticated && isShowEmptyState && !isLoading && !error && (
          <EmptyState
            img={homePageImg}
            heading='Nothing has been searched yet!'
            subHeading='Start by searching using an order ID, customer number, or valet mobile number.'
          />
        )}

        {isAuthenticated &&
          isSearchInvalid &&
          !isLoading &&
          !orders &&
          !newUserData &&
          !error && (
            <EmptyState
              img={InvalidSearchImg}
              heading='No Results Found'
              subHeading='We couldn’t find any records for the selected search field.'
            />
          )}

        {isAuthenticated && !error && (newUserData || orders?.length) && (
          <div className='pageContentContainer'>
            {newUserData ? (
              <NewUser details={newUserData} isWFH={isWFH} />
            ) : (
              <React.Fragment>
                {orders.map((order, index) => {
                  const compToRender =
                    !order.order || !order.order.order_id ? (
                      <EmptyState
                        img={InvalidSearchImg}
                        heading='Something went wrong'
                        subHeading='Please try again later'
                      />
                    ) : (
                      <LifelineOrderDetails
                        order={order}
                        key={index}
                        refundWarning={refundWarning}
                        setOpenHelpSection={setOpenHelpSection}
                        setHelpDSectionOrderId={setHelpDSectionOrderId}
                      />
                    );
                  return compToRender;
                })}

                {hasMoreOrders && (
                  <div className='loadMoreOrders'>
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <Button
                        title={
                          <div className='tw-leading-none tw-flex tw-items-center tw-gap-2'>
                            Load More Orders
                            <Plus size={15} strokeWidth={2.8} />
                          </div>
                        }
                        onClick={loadMoreOrders}
                      />
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        )}

        {isAuthenticated && isBlockingPopup && (
          <BlockingPopupWrapper
            alerts={alerts}
            show={isBlockingPopup}
            showBlockingPopup={showBlockingPopup}
          />
        )}
      </div>

      {openHelpSection && (
        <HelpDeskV2
          setOpenHelpSection={setOpenHelpSection}
          orderId={helpDSectionOrderId}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { ordersState } = state;
  const {
    isNewUI,
    isShowEmptyState,
    isSearchInvalid,
    orders,
    announcements,
    isLoading,
    searchType,
    searchValue,
    hasMoreOrders,
    nextCursor,
    error,
    alerts,
    isBlockingPopup,
    newUserData,
    isWFH,
  } = ordersState;
  const numBanners = announcements ? announcements.length : 0;
  return {
    isNewUI,
    isShowEmptyState,
    isSearchInvalid,
    orders,
    announcements,
    numBanners,
    isLoading,
    searchType,
    searchValue,
    hasMoreOrders,
    nextCursor,
    error,
    alerts,
    isBlockingPopup,
    newUserData,
    isWFH,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getSearchOrders: (data) => dispatch(getSearchOrders(data)),
    setLoading: (isLoading) => dispatch(setLoading(isLoading)),
    showBlockingPopup: (value) => dispatch(showBlockingPopup(value)),
    getRefundWarnings: (data) => dispatch(getRefundWarnings(data)),
    useGetRoles: () => dispatch(useGetRoles()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LifelineDashboard),
);
